import { useState, useEffect } from 'react';
/**
 * Custom hook that provides a debounced state.
 *
 * @template Type - The type of the state.
 * @param {Type} initialState - The initial state value.
 * @param {number} [delay=100] - The debounce delay in milliseconds. Defaults to 100ms.
 * @returns {[Type, Type, React.Dispatch<React.SetStateAction<Type>>]} - Returns an array containing:
 *   - The current state.
 *   - The debounced state.
 *   - The state setter function.
 *
 * @example
 * const [state, debouncedState, setState] = useDebouncedState<string>("initial", 1000);
 */
export const useDebouncedState = (initialState, delay = 100) => {
    const [state, setState] = useState(initialState);
    const [debouncedState, setDebouncedState] = useState(initialState);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedState(state), delay);
        return () => clearTimeout(timer);
    }, [state, delay]);
    return [state, debouncedState, setState];
};
